import { motion } from "framer-motion";
import { Linkedin, Github, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { Logo } from "./Logo";

const navLinks = [
  { name: "Domů", href: "/" },
  { name: "Projekty", href: "/projects" },
  { name: "O mně", href: "/about" },
  { name: "Služby", href: "/services" },
  { name: "Kontakt", href: "/contact" },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      role="navigation"
      aria-label="Hlavní navigace"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/" className="flex items-center" aria-label="Denis Řezníček - Domovská stránka">
              <Logo height={120} />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
                whileHover={{ y: -2 }}
              >
                <Link
                  to={link.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
                >
                  {link.name}
                </Link>
              </motion.div>
            ))}

            {/* Divider */}
            <div className="h-4 w-px bg-border" aria-hidden="true" />

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Divider */}
            <div className="h-4 w-px bg-border" aria-hidden="true" />

            {/* Social Icons */}
            <div className="flex items-center space-x-4">
              <motion.a
                href="https://www.linkedin.com/in/denis-řezníček-151b6a3ab"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                whileHover={{ scale: 1.2, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Navštívit LinkedIn profil"
              >
                <Linkedin size={18} aria-hidden="true" />
              </motion.a>
              <motion.a
                href="https://github.com/rezdenSSPS"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Navštívit GitHub profil"
              >
                <Github size={18} aria-hidden="true" />
              </motion.a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden text-foreground p-2"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.9 }}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? "Zavřít menu" : "Otevřít menu"}
          >
            {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          id="mobile-menu"
          initial={false}
          animate={{ 
            height: isOpen ? "auto" : 0,
            opacity: isOpen ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
          className="md:hidden overflow-hidden"
          aria-hidden={!isOpen}
        >
          <div className="py-4 space-y-4">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.name}
                initial={{ x: -20, opacity: 0 }}
                animate={{ 
                  x: isOpen ? 0 : -20, 
                  opacity: isOpen ? 1 : 0 
                }}
                transition={{ delay: 0.1 * index }}
              >
                <Link
                  to={link.href}
                  className="block text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setIsOpen(false)}
                  tabIndex={isOpen ? 0 : -1}
                >
                  {link.name}
                </Link>
              </motion.div>
            ))}
            
            {/* Theme Toggle in Mobile */}
            <div className="pt-2">
              <ThemeToggle />
            </div>
            
            <div className="flex space-x-4 pt-4">
              <a
                href="https://www.linkedin.com/in/denis-řezníček-151b6a3ab"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
                aria-label="Navštívit LinkedIn profil"
                tabIndex={isOpen ? 0 : -1}
              >
                <Linkedin size={20} aria-hidden="true" />
              </a>
              <a
                href="https://github.com/rezdenSSPS"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground"
                aria-label="Navštívit GitHub profil"
                tabIndex={isOpen ? 0 : -1}
              >
                <Github size={20} aria-hidden="true" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
}
